import axios from 'axios';
import pkceChallenge from 'pkce-challenge';
import BACKEND from '../config';

export interface BackendError {
  statusCode: number;
  internalCode: string;
  message: string;
  timestamp: string;
}

export interface ParsedBackendError {
  title: string;
  details?: string;
}

enum IssuanceType {
  PRE_AUTHORIZED = 'pre-authorized',
  AUTHORIZE = 'authorized',
}

function translateBackendError(error: BackendError): ParsedBackendError {
  const parsedError = {
    title: 'Internal error',
    details: error.message.split(']')[1],
  };
  return parsedError;
}

async function issueCredential(accessToken: string, body: object, url: string) {
  try {
    const headers = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const response = await axios.post(url, body, headers);
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`${'error al enviar codigo'} ${response.data}.`);
    }
    return response;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const parsedError = translateBackendError(
        error.response.data as BackendError,
      );
      return { error: parsedError };
    }
    return { error: { title: 'Unknown' } };
  }
}

function logOut() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('did');
  localStorage.removeItem('code');
  localStorage.removeItem('ID');
}

async function initOpenIdIssuance(
  credentialType: string,
  dni: string,
  mecdProgramCode?: string,
) {
  try {
    const body = {
      dni,
      issuanceType: IssuanceType.PRE_AUTHORIZED,
      credentialType,
      mecdProgramCode,
    };
    const url = `${BACKEND.BASE_URL}/issuance`;
    const response = await axios.post(url, body);
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`${'error al iniciar emisión con oidc4ci'} ${response.data}.`);
    }
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const parsedError = translateBackendError(
          error.response?.data as BackendError,
        );
        return { error: parsedError };
      }
      return {
        error:
            { message: error.message, name: error.name, code: error.code },
      };
    }
    return { error: { title: 'Unknown' } };
  }
}

async function generateCodeChallenge(): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { code_verifier, code_challenge } = await pkceChallenge();
  localStorage.setItem('code_verifier', code_verifier);
  return code_challenge;
}

async function initOpenIdPresentation(scope: string) {
  const codeChallenge = await generateCodeChallenge();
  try {
    const body = {
      scope,
      codeChallenge,
    };
    const url = `${BACKEND.BASE_URL}/presentations`;
    const response = await axios.post(url, body);
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`${'error al iniciar presentatción con oidc4vp'} ${response.data}.`);
    }
    localStorage.setItem('presentation_session_id', response.data.sessionId);
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const parsedError = translateBackendError(
          error.response?.data as BackendError,
        );
        return { error: parsedError };
      }
      return {
        error:
            { message: error.message, name: error.name, code: error.code },
      };
    }
    return { error: { title: 'Unknown' } };
  }
}

async function getPresentationSession() {
  try {
    const sessionId = localStorage.getItem('presentation_session_id');
    const url = `${BACKEND.BASE_URL}/presentations/${sessionId}`;
    const response = await axios.get(url, { headers: { 'ngrok-skip-browser-warning': 'true' } });
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`${'error al obtener presentatción'} ${response.data}.`);
    }
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const parsedError = translateBackendError(
          error.response?.data as BackendError,
        );
        return { error: parsedError };
      }
      return {
        error:
            { message: error.message, name: error.name, code: error.code },
      };
    }
    return { error: { title: 'Unknown' } };
  }
}

async function oidc4vcAuthorize() {
  try {
    const codeVerifier = localStorage.getItem('code_verifier');
    const body = {
      codeVerifier,
    };
    const sessionId = localStorage.getItem('presentation_session_id');
    const url = `${BACKEND.BASE_URL}/presentations/${sessionId}/authorize`;
    const response = await axios.post(url, body);
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`${'error al obtener access_token con flujo oidc4vc'} ${response.data}.`);
    }
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const parsedError = translateBackendError(
          error.response?.data as BackendError,
        );
        return { error: parsedError };
      }
      return {
        error:
            { message: error.message, name: error.name, code: error.code },
      };
    }
    return { error: { title: 'Unknown' } };
  }
}

export {
  issueCredential,
  logOut,
  initOpenIdIssuance,
  initOpenIdPresentation,
  getPresentationSession,
  oidc4vcAuthorize,
};
